import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription 
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Key, CheckCircle2, Loader2, AlertCircle, Vote, BarChart3, XCircle } from "lucide-react";
import api from "../lib/api";

export default function CompleteScrutinyPage() {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [requestInfo, setRequestInfo] = useState(null);
  const [entityKeyPart, setEntityKeyPart] = useState("");
  const [scrutinyResult, setScrutinyResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    loadRequestInfo();
  }, [token]);

  const loadRequestInfo = async () => {
    try {
      const response = await api.get(`/scrutiny/request/${token}`);
      setRequestInfo(response.data);
      
      if (response.data.status === 'pending') {
        toast.info(t('scrutiny.adminNotApproved'));
      } else if (response.data.status === 'completed') {
        toast.info(t('scrutiny.alreadyCompleted'));
      }
    } catch (error) {
      toast.error(t('scrutiny.requestNotFound'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!entityKeyPart.trim()) {
      toast.error(t('scrutiny.enterPrivateKey'));
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post("/scrutiny/complete", {
        token,
        entity_key_part: entityKeyPart.trim()
      });
      
      setScrutinyResult(response.data);
      toast.success(t('scrutiny.completedSuccess'));
      setTimeout(() => {
        navigate(`/consultes/${response.data.consulta_id}/results?autoSendPdf=true`);
      }, 1500);
    } catch (error) {
      const detail = error.response?.data?.detail || t('scrutiny.completeError');
      setErrorMsg(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!requestInfo) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('scrutiny.requestNotFound')}</h1>
          <p className="text-slate-500">{t('scrutiny.requestNotFoundDesc')}</p>
        </div>
      </div>
    );
  }

  if (scrutinyResult) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('scrutiny.completedTitle')}</h1>
          <p className="text-slate-500 mb-6">
            {t('scrutiny.resultsAvailable')}
          </p>
          
          <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm text-slate-600 mb-2">
              <strong>{t('common.consulta')}:</strong> {requestInfo.consulta_title}
            </p>
            <p className="text-sm text-slate-600 mb-2">
              <strong>{t('scrutiny.totalVotes')}:</strong> {scrutinyResult.total_votes}
            </p>
            <p className="text-sm text-slate-600">
              <strong>{t('scrutiny.integrity')}:</strong>{" "}
              <span className={scrutinyResult.integrity_ok ? "text-green-600" : "text-red-600"}>
                {scrutinyResult.integrity_ok ? t('scrutiny.integrityVerified') : t('scrutiny.integrityIssues')}
              </span>
            </p>
          </div>

          <Button
            onClick={() => navigate(`/consultes/${scrutinyResult.consulta_id}/results`)}
            className="w-full bg-blue-600 hover:bg-blue-700"
            data-testid="view-results-btn"
          >
            <BarChart3 className="h-5 w-5 mr-2" />
            {t('scrutiny.viewResults')}
          </Button>
        </motion.div>
      </div>
    );
  }

  if (requestInfo.status === 'pending') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <Loader2 className="h-16 w-16 text-blue-500 mx-auto mb-4 animate-spin" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('scrutiny.waitingApproval')}</h1>
          <p className="text-slate-500 mb-4">
            {t('scrutiny.adminNotApprovedWait')}
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left">
            <p className="text-sm text-amber-800">
              <strong>{t('common.consulta')}:</strong> {requestInfo.consulta_title}
            </p>
            <p className="text-sm text-amber-700 mt-1">
              {t('scrutiny.requestCreated')}: {new Date(requestInfo.created_at).toLocaleDateString('ca-ES')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (requestInfo.status === 'completed') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('scrutiny.alreadyCompletedTitle')}</h1>
          <p className="text-slate-500 mb-4">
            {t('scrutiny.alreadyCompletedDesc')}
          </p>
          <div className="bg-slate-50 rounded-lg p-4 text-left">
            <p className="text-sm text-slate-600">
              <strong>{t('common.consulta')}:</strong> {requestInfo.consulta_title}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-8 max-w-lg w-full"
      >
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
            <Key className="h-7 w-7 text-green-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t('scrutiny.completeTitle')}</h1>
            <p className="text-slate-500">{t('scrutiny.enterKeyToView')}</p>
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Vote className="h-5 w-5 text-green-600 mt-0.5" />
            <div>
              <p className="font-medium text-green-900">{t('scrutiny.adminApprovedRequest')}</p>
              <p className="text-sm text-green-700 mt-1">
                {t('common.consulta')}: <strong>{requestInfo.consulta_title}</strong>
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {t('scrutiny.yourPrivateKeyPart')}
            </label>
            <Textarea
              value={entityKeyPart}
              onChange={(e) => setEntityKeyPart(e.target.value)}
              placeholder={t('scrutiny.enterPrivateKeyPlaceholder')}
              className="h-32 font-mono text-xs"
              data-testid="entity-key-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              {t('scrutiny.keyCombinedDesc')}
            </p>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={submitting || !entityKeyPart.trim()}
            className="w-full bg-green-600 hover:bg-green-700"
            data-testid="complete-scrutiny-btn"
          >
            {submitting ? (
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
            ) : (
              <CheckCircle2 className="h-5 w-5 mr-2" />
            )}
            {t('scrutiny.completeScrutiny')}
          </Button>
        </div>
      </motion.div>

      {/* Error Dialog */}
      <Dialog open={!!errorMsg} onOpenChange={() => {}}>
        <DialogContent className="max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              {t('scrutiny.incorrectKey')}
            </DialogTitle>
            <DialogDescription className="text-slate-600 pt-2">
              {errorMsg}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            {t('scrutiny.reviewAndRetry')}
          </p>
          <div className="flex justify-end pt-2">
            <Button
              onClick={() => { setErrorMsg(""); setEntityKeyPart(""); }}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="error-accept-btn"
            >
              {t('common.accept')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
